import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { VenueSnapshot, PortfolioTotals, PortfolioData } from '@/types/franchise';
import { getBusinessStatus } from '@/lib/business/business-hours';
import { startOfWeek, endOfWeek, differenceInHours, parseISO } from 'date-fns';

/**
 * Hook for fetching and aggregating portfolio data across all user's businesses
 */
export const usePortfolioData = (): PortfolioData & { refetch: () => void } => {
  const { businesses, loadingBusinesses } = useBusinesses();
  const [venues, setVenues] = useState<VenueSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortfolioData = useCallback(async () => {
    if (loadingBusinesses || businesses.length === 0) {
      setVenues([]);
      setLoading(loadingBusinesses);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const businessIds = businesses.map(b => b.id);

      // Fetch all data in parallel for better performance
      const [
        employeesResult,
        reviewsResult,
        shiftsResult,
        timeLogsResult,
      ] = await Promise.all([
        // Employees for all businesses
        supabase
          .from('employees')
          .select('id, business_id, name, contract_status')
          .in('business_id', businessIds),

        // Reviews for all businesses
        supabase
          .from('reviews')
          .select('id, business_id, rating, created_at, customer_email')
          .in('business_id', businessIds),

        // Current week shifts for all businesses
        supabase
          .from('shifts')
          .select('id, business_id, employee_id, start_time, end_time')
          .in('business_id', businessIds)
          .gte('start_time', weekStart.toISOString())
          .lte('end_time', weekEnd.toISOString()),

        // Time logs for today to check who's currently on shift
        supabase
          .from('time_logs')
          .select('id, business_id, employee_id, event_type, scanned_at')
          .in('business_id', businessIds)
          .gte('scanned_at', new Date().toISOString().split('T')[0]),
      ]);

      // Check for errors
      if (employeesResult.error) throw employeesResult.error;
      if (reviewsResult.error) throw reviewsResult.error;
      if (shiftsResult.error) throw shiftsResult.error;
      if (timeLogsResult.error) throw timeLogsResult.error;

      const employees = employeesResult.data || [];
      const reviews = reviewsResult.data || [];
      const shifts = shiftsResult.data || [];
      const timeLogs = timeLogsResult.data || [];

      // Group data by business
      const employeesByBusiness = groupBy(employees, 'business_id');
      const reviewsByBusiness = groupBy(reviews, 'business_id');
      const shiftsByBusiness = groupBy(shifts, 'business_id');
      const timeLogsByBusiness = groupBy(timeLogs, 'business_id');

      // Build venue snapshots
      const venueSnapshots: VenueSnapshot[] = businesses.map(business => {
        const businessEmployees = employeesByBusiness[business.id] || [];
        const businessReviews = reviewsByBusiness[business.id] || [];
        const businessShifts = shiftsByBusiness[business.id] || [];
        const businessTimeLogs = timeLogsByBusiness[business.id] || [];

        // Calculate average rating
        const ratedReviews = businessReviews.filter(r => r.rating != null);
        const averageRating = ratedReviews.length > 0
          ? ratedReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / ratedReviews.length
          : 0;

        // Count employees currently on shift (have arrival log today, no departure)
        const employeesWithArrival = new Set(
          businessTimeLogs
            .filter(log => log.event_type === 'arrival')
            .map(log => log.employee_id)
        );
        const employeesWithDeparture = new Set(
          businessTimeLogs
            .filter(log => log.event_type === 'departure')
            .map(log => log.employee_id)
        );
        const employeesOnShift = [...employeesWithArrival].filter(
          empId => !employeesWithDeparture.has(empId)
        ).length;

        // Determine schedule status based on shifts this week
        const unassignedShifts = businessShifts.filter(s => !s.employee_id);
        const scheduleStatus: 'green' | 'orange' | 'red' =
          unassignedShifts.length === 0 && businessShifts.length > 0 ? 'green' :
          unassignedShifts.length > 0 && unassignedShifts.length < businessShifts.length * 0.5 ? 'orange' :
          'red';

        // Determine overall venue status
        const status: 'healthy' | 'warning' | 'critical' =
          scheduleStatus === 'red' || averageRating < 3 ? 'critical' :
          scheduleStatus === 'orange' || averageRating < 4 ? 'warning' :
          'healthy';

        // Check if business is currently open
        const businessStatus = getBusinessStatus(business.opening_hours || []);
        const isOpen = businessStatus?.isOpen ?? false;

        return {
          businessId: business.id,
          businessName: business.name,
          address: business.address || null,
          status,
          averageRating: Math.round(averageRating * 10) / 10,
          totalReviews: businessReviews.length,
          employeeCount: businessEmployees.length,
          employeesOnShift,
          scheduleStatus,
          isOpen,
        };
      });

      setVenues(venueSnapshots);

    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch portfolio data');
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, [businesses, loadingBusinesses]);

  useEffect(() => {
    fetchPortfolioData();
  }, [fetchPortfolioData]);

  // Calculate aggregated totals
  const totals: PortfolioTotals = useMemo(() => {
    if (venues.length === 0) {
      return {
        venueCount: 0,
        activeVenueCount: 0,
        employeeCount: 0,
        reviewCount: 0,
        averageRating: 0,
        customersEngaged: 0,
        totalHoursThisWeek: 0,
        shiftsThisWeek: 0,
      };
    }

    const totalReviews = venues.reduce((sum, v) => sum + v.totalReviews, 0);
    const totalEmployees = venues.reduce((sum, v) => sum + v.employeeCount, 0);
    const activeVenues = venues.filter(v => v.isOpen).length;

    // Weighted average rating based on review count
    const weightedRatingSum = venues.reduce((sum, v) => sum + (v.averageRating * v.totalReviews), 0);
    const averageRating = totalReviews > 0 ? weightedRatingSum / totalReviews : 0;

    return {
      venueCount: venues.length,
      activeVenueCount: activeVenues,
      employeeCount: totalEmployees,
      reviewCount: totalReviews,
      averageRating: Math.round(averageRating * 10) / 10,
      customersEngaged: 0, // Can be enhanced with unique customer count
      totalHoursThisWeek: 0, // Can be enhanced with shift hours calculation
      shiftsThisWeek: 0, // Can be enhanced
    };
  }, [venues]);

  return {
    venues,
    totals,
    loading: loading || loadingBusinesses,
    error,
    refetch: fetchPortfolioData,
  };
};

/**
 * Helper function to group array by key
 */
function groupBy<T extends Record<string, any>>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

export default usePortfolioData;
